import React, { useMemo, useState } from "react";
import Modal from "components/Modal";
import arrow from "../../image/arrow-left.png";
import { FormProvider, useForm } from "react-hook-form";
import { getUser } from "redux/features/userSliceWithTokenValidation";
import { useSelector } from "react-redux";
import { useGetDossierArchivedQuery, useGetDossierQuery } from "actions/dossiers/queries";
import { useNavigate } from "react-router-dom";
import { ARCHIVE_PATH, PRODUCTS_PATH } from "routes/navigation/navigationPaths";
import "./home.css";
import ButtonWhite from "components/buttons/ButtonWhite";
import {
  RiAddCircleLine,
  RiArchiveLine,
  RiArrowRightLine,
  RiBuilding2Line,
  RiDashboardLine,
  RiFileChartLine,
  RiFolderOpenLine,
  RiPulseLine,
} from "react-icons/ri";

const STAT_CONFIG = [
  {
    key: "created",
    title: "Entreprises créées",
    icon: RiBuilding2Line,
    tone: "teal",
    getCount: (ctx) => ctx.datas,
    onClick: PRODUCTS_PATH,
  },
  {
    key: "active",
    title: "Entreprises actives",
    icon: RiPulseLine,
    tone: "blue",
    getCount: (ctx) => ctx.dossierActif,
    onClick: PRODUCTS_PATH,
  },
  {
    key: "archived",
    title: "Entreprises archivées",
    icon: RiArchiveLine,
    tone: "amber",
    getCount: (ctx) => ctx.dataArchive,
    onClick: ARCHIVE_PATH,
  },
];

function HomePage() {
  const navigate = useNavigate();
  const userStatus = useSelector(getUser);
  const comptableId = userStatus?.id;
  const token = userStatus?.access;

  const { data, refetch, isLoading } = useGetDossierQuery({ comptableId, token });
  const { data: dataArchived } = useGetDossierArchivedQuery({ comptableId, archived: "True" });

  const datas = data?.count || 0;
  const dataArchive = dataArchived?.count || 0;
  const nonArchivedNonActivatedDossiers =
    data?.data.filter((dossier) => !dossier.archived && !dossier.activated) || [];
  const dossierActif = nonArchivedNonActivatedDossiers?.length;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const methods = useForm();

  const statContext = useMemo(
    () => ({ datas, dossierActif, dataArchive }),
    [datas, dossierActif, dataArchive]
  );

  const quickActions = useMemo(
    () => [
      {
        id: "action-create",
        title: "Créer une entreprise",
        description: "Démarrer rapidement un nouveau dossier client",
        icon: <RiAddCircleLine />,
        onClick: () => setIsModalOpen(true),
      },
      {
        id: "action-open",
        title: "Voir les entreprises",
        description: "Accéder à la liste complète des entreprises",
        icon: <RiFolderOpenLine />,
        onClick: () => navigate(PRODUCTS_PATH),
      },
      {
        id: "action-follow",
        title: "Entreprises actives",
        description: "Suivre les dossiers en cours d'activité",
        icon: <RiBuilding2Line />,
        onClick: () => navigate(PRODUCTS_PATH),
      },
      {
        id: "action-archive",
        title: "Entreprises archivées",
        description: "Consulter l'historique et les entreprises fermées",
        icon: <RiFileChartLine />,
        onClick: () => navigate(ARCHIVE_PATH),
      },
    ],
    [navigate]
  );

  const recentDossiers = useMemo(() => {
    const list = Array.isArray(data?.data) ? data.data : [];
    return [...list]
      .filter((dossier) => !dossier?.archived)
      .sort((a, b) => b.id - a.id)
      .slice(0, 5);
  }, [data]);

  const refetchData = async () => {
    if (data) await refetch();
    setIsModalOpen(false);
  };

  return (
    <div className="home-page">
      <div className="home-page-bg" aria-hidden="true">
        <span className="home-blob home-blob-1" />
        <span className="home-blob home-blob-2" />
      </div>

      <section className="home-hero home-reveal">
        <div className="home-hero-content">
          <span className="home-hero-badge">
            <RiDashboardLine /> Tableau de bord
          </span>
          <h1 className="home-hero-title">
            Bonjour {userStatus?.first_name} {userStatus?.last_name}
          </h1>
          <p className="home-hero-subtitle">
            Voici ce qui se passe avec vos données financières
          </p>
          <p className="home-hero-cta-text">
            Centralisez vos entreprises, accélérez vos opérations comptables et gardez une vision
            claire de votre portefeuille.
          </p>
        </div>
        <div className="home-hero-action">
          <ButtonWhite text="+ Créer une entreprise" onClick={() => setIsModalOpen(true)} />
        </div>
      </section>

      <section className="home-cta-banner home-reveal home-reveal-delay-1">
        <div className="home-cta-banner-content">
          <span className="home-cta-badge">Nouveau</span>
          <h2>Passez à une gestion comptable plus rapide et structurée</h2>
          <p>
            Créez une entreprise, suivez les dossiers actifs et accédez en 1 clic à vos entreprises
            archivées.
          </p>
        </div>
        <button type="button" className="home-cta-banner-btn" onClick={() => navigate(PRODUCTS_PATH)}>
          Commencer maintenant <RiArrowRightLine />
        </button>
      </section>

      <FormProvider {...methods}>
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onDossierCreated={refetchData}>
          <h2 className="home-modal-title">
            <img src={arrow} alt="" />
            CREER UN NOUVEAU ENTREPRISE CLIENT
          </h2>
        </Modal>
      </FormProvider>

      <section className="home-stats-section home-reveal home-reveal-delay-2">
        <div className="home-stats-grid">
          {isLoading
            ? [1, 2, 3].map((item) => (
                <div key={`sk-${item}`} className="home-stat-card home-stat-card--skeleton">
                  <div className="home-stat-skeleton-icon" />
                  <div className="home-stat-skeleton-line home-stat-skeleton-line--lg" />
                  <div className="home-stat-skeleton-line" />
                </div>
              ))
            : STAT_CONFIG.map((stat, index) => {
                const Icon = stat.icon;
                const count = stat.getCount(statContext);
                return (
                  <button
                    key={stat.key}
                    type="button"
                    className={`home-stat-card home-stat-card--${stat.tone} home-reveal home-reveal-delay-${index + 2}`}
                    onClick={() => navigate(stat.onClick)}
                  >
                    <div className="home-stat-card-top">
                      <span className="home-stat-icon">
                        <Icon />
                      </span>
                      <RiArrowRightLine className="home-stat-arrow" />
                    </div>
                    <p className="home-stat-count">{count}</p>
                    <h3 className="home-stat-label">{stat.title}</h3>
                  </button>
                );
              })}
        </div>
      </section>

      <section className="home-actions-section home-reveal home-reveal-delay-3">
        <h2 className="home-section-title">Actions rapides</h2>
        <div className="home-actions-grid">
          {quickActions.map((action, index) => (
            <button
              key={action.id}
              type="button"
              className={`home-action-card home-reveal home-reveal-delay-${index + 4}`}
              onClick={action.onClick}
            >
              <span className="home-action-icon">{action.icon}</span>
              <span className="home-action-content">
                <strong>{action.title}</strong>
                <small>{action.description}</small>
              </span>
              <RiArrowRightLine className="home-action-arrow" />
            </button>
          ))}
        </div>
      </section>

      <section className="home-actions-section home-reveal home-reveal-delay-4">
        <div className="home-section-header">
          <h2 className="home-section-title">Entreprises récentes</h2>
          <button type="button" className="home-section-link" onClick={() => navigate(PRODUCTS_PATH)}>
            Voir tout
          </button>
        </div>
        <div className="home-recent-list">
          {recentDossiers.length === 0 ? (
            <div className="home-empty-state">Aucune entreprise récente pour le moment.</div>
          ) : (
            recentDossiers.map((dossier, index) => (
              <div
                key={dossier.id}
                className={`home-recent-item home-reveal home-reveal-delay-${index + 5}`}
              >
                <div className="home-recent-info">
                  <strong>{dossier.nom_entreprise || "Entreprise"}</strong>
                  <div className="home-recent-subtext">
                    {dossier.activated ? "Active" : "En attente d'activation"}
                  </div>
                </div>
                <button
                  type="button"
                  className="home-open-btn"
                  onClick={() => navigate(`/client/${dossier.id}`)}
                >
                  Ouvrir
                </button>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

export default HomePage;
